import { Col, DatePicker, Form, Input, Row } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import DefaultButton from '../../../components/buttons/DefaultButton';
import RESOURCE_CONFIGS from '../../../constants/resourceConfig';
import { REGEX_ALL_ALLOWED_SAFE_FILTER, REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER } from '../../../constants/regex';

interface PriceRecordFormProps {
  form: FormInstance;
  handleNext: () => void;
  handleCancel: () => void;
  inspectorOptions?: any[];
  isModal: boolean;
  isCitizen?: boolean;
  triggeredFromTask: boolean;
  taskData: any;
  disabled?: boolean;
  isViewMode?: boolean;
}

const CollectionSessionInfoForm: React.FC<PriceRecordFormProps> = ({
  form,
  handleNext,
  isViewMode,
  handleCancel,
}) => {
  const { t } = useTranslation('price');

  const handleContinue = () => {
    handleNext();
  };

  const disableFutureDates = (current: any) => {
    return current && current > dayjs().endOf('day');
  };

  return (
    <div className={`m-0 w-full ${isViewMode ? 'p-2' : 'p-8'}`}>
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('inspection:generalInformation')}
        </h2>
      </div>
      <Form
        form={form}
        name="collectionSessionInfoDataForm"
        layout="vertical"
        onFinish={handleNext}
        initialValues={{
          date: dayjs(),
        }}
      >
        <Row gutter={[24, 8]}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('nameOfServiceStation')}
              name="nameOfServiceStation"
              rules={[{ required: true, message: t('fieldRequired') }]}
            >
              <Input placeholder={t('nameOfServiceStation')} disabled={true} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={t('nameOfAssistedPerson')}
              name="nameOfAssistedPerson"
              validateFirst
              rules={[
                { required: true, message: t('nameOfAssistedPersonRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('nameOfAssistedPersonSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    // Allows letters, space and fullstop
                    const regex = REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER;
                    return regex.test(value)
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }
    
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 3) {
                      return Promise.reject(t('nameOfAssistedPersonrMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }
    
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('nameOfAssistedPersonMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder={t('nameOfAssistedPerson')} disabled={isViewMode} />
            </Form.Item>
          </Col>

          <Col xs={24} md={24}>
            <Form.Item
              label={t('volumetricContainerData')}
              name="volumetricContainerData"
              validateFirst
              rules={[
                { required: true, message: t('volumetricContainerDataRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('volumetricContainerDataSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters
                    // const regex = /^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñÜü\s,.\-!?;:()"'&@#$%^*+=/]+$/;
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value)
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }
    
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 3) {
                      return Promise.reject(t('volumetricContainerDataMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }
    
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 200) {
                      return Promise.reject(t('volumetricContainerDataMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.TextArea
                placeholder={t('volumetricContainerDataPlaceholder')}
                disabled={isViewMode}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={t('startTime')}
              name="startTime"
              rules={[{ required: true, message: t('startTimeRequired') }]}
            >
              <DatePicker
                className="w-full"
                showTime={{ format: RESOURCE_CONFIGS.DATE_TIME.TIME_ONLY }}
                format={RESOURCE_CONFIGS.DATE_TIME.DATE_WITH_TIME}
                use12Hours={false}
                disabledDate={disableFutureDates}
                disabled={isViewMode}
                inputReadOnly={true}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={t('endTime')}
              name="endTime"
              dependencies={['startTime']}
              rules={[
                { required: true, message: t('endTimeRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue('startTime');
                    if (!value || !start || value.isAfter(start)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('endTimeMustBeAfterStart')));
                  },
                }),
              ]}
            >
              <DatePicker
                className="w-full"
                showTime={{ format: RESOURCE_CONFIGS.DATE_TIME.TIME_ONLY }}
                format={RESOURCE_CONFIGS.DATE_TIME.DATE_WITH_TIME}
                use12Hours={false}
                disabledDate={disableFutureDates}
                disabled={isViewMode}
                inputReadOnly={true}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {!isViewMode && (
        <div className="mt-6 flex w-full flex-col justify-end gap-4 pt-4 md:ml-auto md:w-1/2 md:flex-row md:pl-3">
          <DefaultButton handleOnClick={handleCancel} width="100%">
            {t('cancel')}
          </DefaultButton>
          <PrimaryButton
            handleOnClick={handleContinue}
            disabled={isViewMode}
            width="100%"
            hideBgGradient={true}
          >
            {t('goNext')}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default CollectionSessionInfoForm;
